import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import propTypes from 'prop-types';
import { PanelRightClose, Search, X } from 'lucide-react';
import CarrierSidebarItem from './CarrierSidebarItem';
import { cn } from 'lib/utils';

// pr-0! overrides show-scrollbar's 10px gutter so the selected row spans edge to edge
const LIST_SCROLL_CLASSNAME =
  'show-scrollbar min-h-0 flex-1 overflow-y-auto pr-0!';

/**
 * searchable list of carriers used as the right sidebar of the OEP Updates
 * page. the parent already dropped `Hide_from_OEP_Updates` carriers and sorted
 * the remaining ones so those with actual updates come first. supports
 * arrow-up/down navigation between items (focus jumps only across
 * visible-after-filter carriers) plus escape to clear the search.
 *
 * collapsible: collapsed it becomes a narrow rail with a round search button
 * (expands and focuses the search box) and one avatar per carrier (selects
 * that carrier and expands). keeps its own search-query state locally;
 * selection and the collapsed flag are lifted to the parent.
 * @param carriers - already-filtered and pre-sorted array of carriers
 * @param selectedName - the currently selected carrier's name (or null)
 * @param onSelect - called with the carrier name when a row is chosen
 * @param collapsed - render the narrow avatar rail instead of the full list
 * @param onCollapsedChange - called with the next collapsed flag; omit to
 *   make the sidebar non-collapsible (e.g. inside the mobile sheet)
 */
export default function CarrierSidebar({
  carriers,
  selectedName,
  onSelect,
  collapsed = false,
  onCollapsedChange
}) {
  const [query, setQuery] = useState('');
  const itemRefs = useRef({});
  const searchInputRef = useRef(null);
  const focusSearchOnExpandRef = useRef(false);
  const isCollapsible = typeof onCollapsedChange === 'function';

  const filteredCarriers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return carriers;
    return carriers.filter(c => c.name.toLowerCase().includes(normalized));
  }, [carriers, query]);

  // the rail shows every carrier; the search only filters the expanded list
  const visibleCarriers = collapsed ? carriers : filteredCarriers;

  // the input only mounts once expanded, so focus it after that render
  useEffect(() => {
    if (collapsed || !focusSearchOnExpandRef.current) return;
    focusSearchOnExpandRef.current = false;
    searchInputRef.current?.focus();
  }, [collapsed]);

  function handleExpandToSearch() {
    focusSearchOnExpandRef.current = true;
    onCollapsedChange(false);
  }

  function handleRailSelect(name) {
    onSelect(name);
    onCollapsedChange(false);
  }

  const focusIndex = useCallback(index => {
    const carrier = visibleCarriers[index];
    if (!carrier) return;
    itemRefs.current[carrier.name]?.focus?.();
  }, [visibleCarriers]);

  function handleListKeyDown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const activeName = document.activeElement?.dataset?.carrierName;
    const currentIndex = visibleCarriers.findIndex(c => c.name === activeName);
    if (currentIndex === -1) {
      focusIndex(0);
      event.preventDefault();
      return;
    }
    const delta = event.key === 'ArrowDown' ? 1 : -1;
    const next = currentIndex + delta;
    if (next < 0 || next >= visibleCarriers.length) return;
    focusIndex(next);
    event.preventDefault();
  }

  function handleSearchKeyDown(event) {
    if (event.key === 'Escape' && query) {
      setQuery('');
      event.preventDefault();
    } else if (event.key === 'ArrowDown' && filteredCarriers.length > 0) {
      focusIndex(0);
      event.preventDefault();
    }
  }

  const registerRef = useCallback((name, node) => {
    if (node) itemRefs.current[name] = node;
    else delete itemRefs.current[name];
  }, []);

  return (
    <aside
      data-tour='oep-carriers-sidebar'
      className='flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border border-grey-lightest bg-white'
    >
      {collapsed ? (
        <div className='flex shrink-0 justify-center border-b border-grey-lightest py-3'>
          <button
            type='button'
            onClick={handleExpandToSearch}
            aria-label='Search carriers'
            title='Search carriers'
            className='flex size-9 cursor-pointer items-center justify-center rounded-full border border-grey-lightest bg-white p-0 text-grey-light transition-colors hover:border-primary hover:text-primary-darkened focus:outline-none focus-visible:ring-2 focus-visible:ring-primary'
          >
            <Search className='size-4' aria-hidden />
          </button>
        </div>
      ) : (
        <div className='flex shrink-0 items-center gap-2 border-b border-grey-lightest p-3'>
          <label className='relative flex flex-1 items-center'>
            <Search
              aria-hidden
              className='pointer-events-none absolute left-3 size-4 text-grey-light'
            />
            <input
              ref={searchInputRef}
              type='search'
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder='Search carriers...'
              aria-label='Search carriers'
              className='w-full appearance-none rounded-md border border-grey-lightest bg-white py-2 pl-9 pr-8 text-sm text-gray-800 placeholder:text-grey-light focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden [&::-webkit-search-results-button]:hidden [&::-webkit-search-results-decoration]:hidden'
            />
            {query && (
              <button
                type='button'
                onClick={() => setQuery('')}
                aria-label='Clear search'
                className='absolute right-2 flex size-5 cursor-pointer items-center justify-center rounded-sm border-0 bg-transparent p-0 text-primary/60 transition-colors hover:text-primary-darkened focus:outline-none focus-visible:ring-1 focus-visible:ring-primary'
              >
                <X className='size-4' aria-hidden />
              </button>
            )}
          </label>
          {isCollapsible && (
            <button
              type='button'
              onClick={() => onCollapsedChange(true)}
              aria-label='Collapse carriers sidebar'
              title='Collapse'
              className='flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent p-0 text-grey-light transition-colors hover:bg-primary-lighter hover:text-primary-darkened focus:outline-none focus-visible:ring-1 focus-visible:ring-primary'
            >
              <PanelRightClose className='size-4' aria-hidden />
            </button>
          )}
        </div>
      )}

      <div className={LIST_SCROLL_CLASSNAME}>
        {visibleCarriers.length === 0 ? (
          <div className='flex h-full flex-col items-center justify-center gap-2 p-6 text-center'>
            <p className='m-0 text-sm text-gray-600'>
              No carriers match &ldquo;{query}&rdquo;
            </p>
            <button
              type='button'
              onClick={() => setQuery('')}
              className='text-xs font-medium text-primary hover:underline focus:outline-none focus-visible:underline'
            >
              Clear search
            </button>
          </div>
        ) : (
          <ul
            role='listbox'
            aria-label='OEP carriers'
            onKeyDown={handleListKeyDown}
            className={cn('m-0 flex flex-col p-0', collapsed && 'py-1.5')}
          >
            {visibleCarriers.map(carrier => (
              <CarrierSidebarItem
                key={carrier.id || carrier.name}
                carrier={carrier}
                isSelected={carrier.name === selectedName}
                onSelect={collapsed ? handleRailSelect : onSelect}
                collapsed={collapsed}
                buttonRef={node => {
                  registerRef(carrier.name, node);
                  if (node) node.dataset.carrierName = carrier.name;
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

CarrierSidebar.propTypes = {
  carriers: propTypes.array.isRequired,
  selectedName: propTypes.string,
  onSelect: propTypes.func.isRequired,
  collapsed: propTypes.bool,
  onCollapsedChange: propTypes.func
};
